import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectPortalVerifyCodeStatus } from "../../features/portal/portal-slice";
import { verifyPortalCode } from "../../features/portal/slice-extensions/portal-slice-extension";
import { ROUTES } from "../../routes";

interface PortalCodeEntryProps {
  email: string;
}

/** Types the 6-digit code from the sign-in email instead of clicking the link. */
const PortalCodeEntry = ({ email }: PortalCodeEntryProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const status = useAppSelector(selectPortalVerifyCodeStatus);
  const [code, setCode] = useState("");

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim()) {
      return;
    }
    dispatch(verifyPortalCode({ email, code: code.trim() }))
      .unwrap()
      .then(() => navigate(ROUTES.PORTAL, { replace: true }))
      .catch(() => undefined);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 border-t border-grey-90 pt-4 dark:border-primary-10">
      <label className="label">
        <span className="label-text">
          {t("portal.codeLabel", "Or type the code from the email")}
        </span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          className="input tracking-[0.3em]"
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
          placeholder="123456"
        />
      </label>
      {status === "failed" && (
        <p className="mt-2 text-sm text-red-700 dark:text-red-400">
          {t(
            "portal.codeInvalid",
            "That code didn't work. Check it, or request a new email.",
          )}
        </p>
      )}
      <button
        type="submit"
        className="btn btn-primary mt-3 w-full"
        disabled={status === "loading" || code.trim().length < 6}
      >
        {status === "loading"
          ? t("portal.signingIn", "Signing you in…")
          : t("portal.verifyCode", "Sign in with code")}
      </button>
    </form>
  );
};

export default PortalCodeEntry;
